import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { captureException, setUser } from '@sentry/react';
import { reset as amplitudeReset } from '@amplitude/analytics-browser';
import { clearLastActivityAt } from 'app/auth/sessionTimeout';
import { PROCORE_TOKENS_KEY, resetProcoreSlice } from 'app/pages/store/procoreSlice';
import type { IdentifyUserMessage } from 'app/pages/mobile/pages/mobile-transactions/__types__/MobileOptionMenu.types';
import type { RootState } from 'app/store/rootReducer';
import { Auth } from 'aws-amplify/';
import logger from 'util/logging';
import serializeError, { ErrorLike } from 'util/serializeError';
import { resetFundingSlice } from 'app/pages/store/fundingSlice';
import { resetAgaveSlice } from 'app/pages/store/agaveSlice';
import { resetBudgetWidgetSlice } from 'app/pages/store/budgetWidgetSlice';
import { resetDownloadTransactions } from 'app/pages/store/downloadTransactionsSlice';
import { resetGroupsSlice } from 'app/pages/store/groupsSlice';
import { resetPriorityActionsSlice } from 'app/pages/store/priorityActionsSlice';
import { resetReviewOnboardSlice } from 'app/pages/store/reviewOnboardSlice';
import { resetSpendRulesSlice } from 'app/pages/store/spendRulesSlice';
import { resetSpendWidgetSlice } from 'app/pages/store/spendWidgetSlice';
import { resetTopMerchantsWidgetSlice } from 'app/pages/store/topMerchantsWidgetSlice';
import { resetTransactionsSlice } from 'app/pages/store/transactionsSlice';
import { resetReimbursementsSlice } from 'app/pages/store/reimbursementsSlice';
import { userService } from 'app/services/user.service';
import { v4 as uuidv4 } from 'uuid';
import { clearCompanySelection } from 'util/helpers';
import { paidolService } from 'app/services/paidol.service';
import { authenticateWithPasskey } from 'app/auth/passkeyClient';
import { isMobileAppShell } from 'app/auth/nativePasskeyBridge';
import { recordPasskeyCreated } from 'app/auth/passkeyPromptChoice';
import { setIsSwitching, setSelectedPaidolId, resetUserCompaniesSlice } from './userCompaniesSlice';
import { resetUserProfileSlice } from './userProfileSlice';
import { resetDialogSlice } from './dialogSlice';
import { resetFeatureFlagsSlice } from './featureFlagsSlice';
import { resetOnboardSlice } from './onboardSlice';
import { resetWalletSlice } from './walletSlice';
import { resetSearchAndFilter } from './filtering/searchAndFilterSlice';
import { resetFlexCodingSlice } from 'app/pages/store/flexCodingSlice';
import { resetFlexFieldSlice } from 'app/pages/store/flexFieldSlice';
import { resetJobCodeSlice } from 'app/pages/store/jobCodeSlice';
import { resetSplitDraftsSlice } from 'app/store/splitDraftsSlice';
import { clearFilterOptions } from 'app/pages/store/transactionFilterOptionsSlice';
import { resetProjectGroupsSlice } from 'app/pages/store/projectGroupSlice';
import { resetUsersSlice } from 'app/pages/store/usersSlice';
import { resetUnreadMessagesSlice } from 'app/pages/overview/store/unreadMessagesSlice';
import { resetOverviewPriorityActionsSlice } from 'app/pages/overview/store/priorityActionsSlice';
import { resetDataExportSlice } from 'app/pages/payables/store/dataExportSlice';
import { resetProfileSlice } from 'app/pages/profile/store/profileSlice';
import {
  MY_CARDS_PAGE_TOKEN_KEY,
  resetCardSlice,
  resetGroupCards,
  resetAssinableCards,
  resetSpecificCard,
} from 'app/pages/store/CardDataSlice/cardDataSlice';
import { invalidateAgaveOptionsCache } from 'app/hooks/agaveCodingDropdownCache';
import { invalidateFlexOptionsCache } from 'app/hooks/flexCodingDropdownCache';
import { clearWaitingOnCache } from 'app/hooks/waitingOnCache';
import { fileAccessService } from 'app/services/fileAccess.service';

export const CognitoError = {
  NotAuthorized: 'NotAuthorizedException',
  UserNotFound: 'UserNotFoundException',
  UsernameExists: 'UsernameExistsException',
  CodeMismatch: 'CodeMismatchException',
  LimitExceeded: 'LimitExceededException',
  PasswordReset: 'PasswordResetRequiredException',
  InvalidParameter: 'InvalidParameterException',
  LambdaError: 'UserLambdaValidationException',
  UserNotConfirmed: 'UserNotConfirmedException',
};

export const DEFAULT_LANDING_PAGE = '/landing';
const LOCALSTORAGE_KEY = 'remember_me';
export const IS_INIITAL_ACCESS_LOCAL_STORAGE_KEY = 'isInitialAccess';
export const PAIDOL_SESSION_ID_KEY = 'paidolSessionId';

/**
 * The paidol id a token is scoped to: the one `cognito:groups` entry that is a
 * bare paidol id. Role groups carry `paidol:ROLE`, Highnote groups `HIGHNOTE:`,
 * and identity-provider groups are `<userPoolId>_<provider>`, where the pool id
 * is itself `<region>_<id>`; paidol ids are UUIDs and carry neither `:` nor `_`.
 * One predicate for every reader: three drifting copies of this heuristic is
 * the same class of bug SEE-1583 fixes on the server.
 */
export const paidolIdFromGroups = (cognitoGroups: string[] | undefined): string | undefined =>
  cognitoGroups?.find((group) => !group.includes(':') && !group.includes('_'));
export const SESSION_ID_KEY = `sessionId`;

// Extend Window interface to include ReactNativeWebView
interface WindowWithReactNativeWebView extends Window {
  ReactNativeWebView?: {
    postMessage: (message: string) => void;
  };
}

// Regex pattern to match SpeedchainMobile user agent with semantic versioning
// Matches versions like: "1", "1.0", "1.1", "2.0", "2.5.10", etc.
// Pattern: SpeedchainMobile/<identifier>/<version>
// Version: one or more digits, optionally followed by dot and more digits
const MOBILE_UA_REGEX = /SpeedchainMobile\/[^/]+\/\d+(\.\d+)*/;

const isMobileApp = (): boolean => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  return MOBILE_UA_REGEX.test(navigator.userAgent);
};

/** Auth-invalid Cognito errors that warrant clearing mobile app localStorage */
const AUTH_INVALID_CODES = new Set([
  CognitoError.NotAuthorized,
  CognitoError.UserNotFound,
  CognitoError.PasswordReset,
]);

const isAuthInvalidError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const err = error as { code?: string; name?: string };
  const codeOrName = err.code ?? err.name;
  return codeOrName != null && AUTH_INVALID_CODES.has(codeOrName);
};

/**
 * Checks if a localStorage key should be excluded from backup due to containing sensitive data.
 * @param key - The localStorage key to check
 * @returns true if the key should be excluded, false otherwise
 */
export const isSensitiveKey = (key: string): boolean => {
  // Whitelist: Amplify/Cognito keys are required for session restore in mobile
  if (key.startsWith('CognitoIdentityServiceProvider.')) {
    return false;
  }

  // Known sensitive keys
  const sensitiveKeys = new Set([
    PROCORE_TOKENS_KEY,
    SESSION_ID_KEY,
    PAIDOL_SESSION_ID_KEY,
    `${SESSION_ID_KEY}_federated`,
    MY_CARDS_PAGE_TOKEN_KEY,
  ]);

  if (sensitiveKeys.has(key)) {
    return true;
  }

  // Pattern-based detection for token-like keys
  const tokenPatterns = [
    /token/i, // matches "token", "Token", "TOKEN", etc.
    /session/i, // matches "session", "Session", "SESSION", etc.
    /auth/i, // matches "auth", "Auth", "AUTH", etc.
    /credential/i, // matches "credential", "Credentials", etc.
    /password/i, // matches "password", "Password", etc.
    /secret/i, // matches "secret", "Secret", etc.
    /(api[_-]?)?key/i, // matches "apiKey", "api_key", "API_KEY", "accessKey", etc.
  ];

  return tokenPatterns.some((pattern) => pattern.test(key));
};

/**
 * Backs up localStorage data to the mobile app synchronously.
 * Returns a Promise that always resolves (never rejects) to prevent interrupting auth flows.
 * Errors are logged but not propagated to callers.
 * Sensitive keys (tokens, sessions, credentials) are excluded from the backup.
 * @returns Promise that resolves when backup is complete or when an error occurs (error is logged)
 */
const backupLocalStorage = async (): Promise<void> => {
  if (!isMobileApp() || typeof window === 'undefined') {
    return;
  }

  const reactNativeWebView = (window as WindowWithReactNativeWebView).ReactNativeWebView;
  if (!reactNativeWebView) {
    return;
  }

  try {
    const localStorageData: Record<string, string> = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && !isSensitiveKey(key)) {
        localStorageData[key] = localStorage.getItem(key) || '';
      }
    }

    reactNativeWebView.postMessage(
      JSON.stringify({
        type: 'LOCALSTORAGE_BACKUP',
        data: localStorageData,
      })
    );
  } catch (error) {
    logger().error('[Web] Error notifying mobile app to backup localStorage:', error);
    // Swallow error to prevent interrupting authentication flows
  }
};

const notifyMobileAppToClearLocalStorage = (): void => {
  if (!isMobileApp() || typeof window === 'undefined') {
    return;
  }

  const reactNativeWebView = (window as WindowWithReactNativeWebView).ReactNativeWebView;
  if (!reactNativeWebView) {
    return;
  }

  try {
    reactNativeWebView.postMessage(
      JSON.stringify({
        type: 'CLEAR_LOCALSTORAGE',
      })
    );
  } catch (error) {
    // eslint-disable-next-line no-console
    logger().error('[Web] Error notifying mobile app to clear localStorage:', error);
  }
};

/**
 * Relays the signed-in user to the native app over the WebView bridge so native
 * Sentry events — which carry the real device/OS/memory context web events lack —
 * become attributable to a specific customer. No-op outside the mobile app.
 * Errors are swallowed to avoid interrupting auth flows.
 */
export const identifyMobileAppUser = (user: { id?: string; email?: string; username?: string }): void => {
  if (!isMobileApp() || typeof window === 'undefined') {
    return;
  }

  const reactNativeWebView = (window as WindowWithReactNativeWebView).ReactNativeWebView;
  if (!reactNativeWebView) {
    return;
  }

  try {
    const message: IdentifyUserMessage = {
      type: 'IDENTIFY_USER',
      id: user.id,
      email: user.email,
      username: user.username,
    };
    reactNativeWebView.postMessage(JSON.stringify(message));
  } catch (error) {
    logger().error('[Web] Error identifying user to mobile app:', error);
  }
};

export const triggerBackupLocalStorage = createAsyncThunk('auth/triggerBackupLocalStorage', async () => {
  await backupLocalStorage();
});

const withTimeout = <T>(promise: Promise<T>, ms: number, label: string): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error(`${label} timed out after ${ms}ms`));
    }, ms);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }) as Promise<T>;
};

export interface UserAttributes {
  email: string;
  sub: string;
  family_name?: string;
  given_name?: string;
  preferred_username?: string;
  email_verified?: boolean;
  /** JSON string Cognito sets on IdP-linked accounts; presence = SSO-linked. */
  identities?: string;
}

export interface AuthState {
  isAuthStateUnknown: boolean; // true when app starts until first auth check
  didSessionExpire: boolean; // true if getCurrentUser returns error while signed in
  didUserSignOut: boolean;
  username?: string;
  user?: UserAttributes;
  paidolSessionId?: string;
  refreshingUserSession: boolean;
  /**
   * Epoch ms (UTC) of the last successful step-up MFA in this session (SEE-221).
   * Set after a password user clears an SMS OTP; read by the bank-link gate to
   * decide whether MFA is still fresh. Left undefined on a fresh password login
   * (single factor until proven otherwise) and cleared on sign-out. SSO/passkey
   * sessions do not need this — they are MFA by login method (see sessionSecurity).
   */
  mfaVerifiedAt?: number;
  /**
   * Short-lived signed grant returned by verifyBankLinkStepUp after a successful
   * SMS OTP (SEE-221). The Plaid resolver REQUIRES this for single-factor
   * sessions before issuing a link token, which is what makes the gate
   * unbypassable. Held only in memory; cleared on sign-in/out alongside
   * mfaVerifiedAt. SSO/passkey sessions never get one (gate skipped, server skips).
   */
  stepUpGrant?: string;
  // SEE-1350: post-login SSO account verification found a divergence between
  // what the user typed and who Microsoft authenticated. Non-null renders the
  // mismatch screen (SsoAccountVerificationGate).
  ssoMismatch?: {
    typedEmail: string;
    authenticatedIdentity: string;
    provider: string;
    /** Web-only escalation: the same wrong account returned after a switch. */
    repeat: boolean;
  };
}

export const initialState: AuthState = {
  isAuthStateUnknown: true,
  didSessionExpire: false,
  didUserSignOut: false,
  username: localStorage.getItem(LOCALSTORAGE_KEY) || undefined,
  user: undefined,
  paidolSessionId: undefined,
  refreshingUserSession: false,
};

export const getCurrentUser = createAsyncThunk('auth/getCurrentUser', async () => {
  try {
    const response = await Auth.currentAuthenticatedUser({ bypassCache: true });
    const currentSession = await Auth.currentSession();
    const { payload } = currentSession.getIdToken();
    if (payload['custom:original_sub']) {
      response.attributes.sub = payload['custom:original_sub'];
    }
    // SSO tokens carry a server-minted session id (custom:session_id) matching the
    // SelectedPaidolSession row. Persist it so savePaidolSessionId (which reads
    // SESSION_ID_KEY) can establish the session for federated users too.
    if (payload['custom:session_id']) {
      localStorage.setItem(SESSION_ID_KEY, payload['custom:session_id'] as string);
    }
    return response.attributes;
  } catch (error) {
    if (isMobileApp() && isAuthInvalidError(error)) {
      notifyMobileAppToClearLocalStorage();
    }
    throw error;
  }
});

interface SignInArgs {
  username: string;
  password: string;
  remember: boolean;
  email?: string;
}

interface FederatedSignInArgs {
  federatedProvider: string;
}

export const federatedSignIn = createAsyncThunk<unknown, FederatedSignInArgs, { rejectValue: ErrorLike }>(
  'auth/federatedSignIn',
  async ({ federatedProvider }, { rejectWithValue, dispatch }) => {
    let sessionId = localStorage.getItem(`${SESSION_ID_KEY}_federated`);
    if (!sessionId) {
      sessionId = createSessionId();
    }

    // Store sessionId in localStorage for retrieval after redirect
    localStorage.setItem(`${SESSION_ID_KEY}_federated`, sessionId);

    return Auth.federatedSignIn({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      provider: federatedProvider as any,
    }).then((response) => {
      return response;
    });
  }
);

// New function to handle sessionId after federated authentication
export const handleFederatedSession = createAsyncThunk<unknown, void, { rejectValue: ErrorLike }>(
  'auth/handleFederatedSession',
  async (_, { rejectWithValue, dispatch, getState }) => {
    // Read the server-minted session id from the token; it matches the
    // SelectedPaidolSession row created on the hosted-UI login.
    let sessionId: string;
    try {
      const currentSession = await Auth.currentSession();
      const claimSessionId = currentSession.getIdToken().payload['custom:session_id'] as string | undefined;
      if (!claimSessionId) {
        // Token predates the server-side session claim (e.g. a cached token during
        // rollout); the client-generated fallback has no backing row, so surface it.
        console.warn(
          'custom:session_id claim missing from token — falling back to client-generated session id'
        );
      }
      sessionId = claimSessionId || createSessionId();
    } catch {
      sessionId = createSessionId();
    }
    // Store sessionId in localStorage for retrieval after redirect
    localStorage.setItem(`${SESSION_ID_KEY}_federated`, sessionId);
    localStorage.setItem(SESSION_ID_KEY, sessionId);

    // Get the current user's information from the state
    const state = getState() as RootState;
    const user = state.auth.user;

    if (user) {
      // Extract paidolId from the user's groups (this is set in preTokenGeneration)
      const cognitoGroups = (user as unknown as Record<string, unknown>)['cognito:groups'] as string[];
      const paidolId = paidolIdFromGroups(cognitoGroups) || cognitoGroups?.[0];
      const userId = user.sub;

      if (paidolId && userId) {
        try {
          await paidolService.createOrGetPaidolSession({ sessionId, paidolId, userId });
          // Successfully handled federated session
        } catch (error) {
          return rejectWithValue(serializeError(error as Error));
        }
      }
    }
  }
);

export const signIn = createAsyncThunk<UserAttributes, SignInArgs, { rejectValue: ErrorLike }>(
  'auth/signIn',
  async ({ username, password, remember, email }, { rejectWithValue, dispatch }) => {
    let sessionId = localStorage.getItem(SESSION_ID_KEY);
    if (!sessionId) {
      sessionId = createSessionId();
    }
    localStorage.setItem(IS_INIITAL_ACCESS_LOCAL_STORAGE_KEY, 'true');

    return Auth.signIn(username, password, {
      session_id: localStorage.getItem(SESSION_ID_KEY) ?? '',
    })
      .then(async (response) => {
        if (remember) {
          // Save the email (what the user typed) instead of the resolved Cognito username
          dispatch(rememberUsername({ username: email || username, persist: true }));
        } else {
          dispatch(forgetUsername());
        }
        const cognitoGroups = response.signInUserSession?.accessToken?.payload['cognito:groups'] as string[];
        if (cognitoGroups && cognitoGroups.length > 0) {
          const paidolId = paidolIdFromGroups(cognitoGroups) ?? cognitoGroups[0];
          const userId = response.signInUserSession?.accessToken?.payload.sub;
          const currentSessionId = localStorage.getItem(SESSION_ID_KEY);
          if (currentSessionId) {
            try {
              await paidolService.createOrGetPaidolSession({ sessionId: currentSessionId, paidolId, userId });
            } catch (sessionError) {
              captureException(sessionError);
            }
          }
        }

        await backupLocalStorage();

        return response.attributes;
      })
      .catch((error) => {
        captureException(error, {
          extra: { username },
        });
        return rejectWithValue(serializeError(error));
      });
  }
);

/**
 * SEE-213: sign in with a passkey (returning-user flow). Mirrors `signIn`'s
 * post-auth bootstrap (paidol session + remember + backup) but authenticates
 * via the Cognito CUSTOM_AUTH WebAuthn challenge instead of a password.
 * Rejects on NO_PASSKEY / cancel / failure so the caller falls back to password.
 */
export const signInWithPasskey = createAsyncThunk<
  UserAttributes,
  { username: string; email?: string; remember?: boolean },
  { rejectValue: ErrorLike }
>('auth/signInWithPasskey', async ({ username, email, remember }, { rejectWithValue, dispatch }) => {
  let sessionId = localStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = createSessionId();
  }
  localStorage.setItem(IS_INIITAL_ACCESS_LOCAL_STORAGE_KEY, 'true');

  try {
    // SEE-1265: same clientMetadata.session_id the password flow sends, so
    // PreTokenGeneration creates this browser's SelectedPaidolSession row and
    // the company switcher has a row to rotate.
    await authenticateWithPasskey(username, sessionId);
    const cognitoUser = await Auth.currentAuthenticatedUser({ bypassCache: true });
    const session = cognitoUser.signInUserSession ?? (await Auth.currentSession());

    // They just signed in WITH a passkey — they clearly have one, so the
    // post-login enrollment prompt must never show for this account again
    // (covers passkeys created on another device / synced via iCloud).
    if (cognitoUser.attributes?.sub) {
      recordPasskeyCreated(cognitoUser.attributes.sub);
    }

    if (remember) {
      dispatch(rememberUsername({ username: email || username, persist: true }));
    } else {
      dispatch(forgetUsername());
    }

    const cognitoGroups = session?.accessToken?.payload['cognito:groups'] as string[] | undefined;
    if (cognitoGroups && cognitoGroups.length > 0) {
      const paidolId = paidolIdFromGroups(cognitoGroups) ?? cognitoGroups[0];
      const userId = session.accessToken.payload.sub;
      const currentSessionId = localStorage.getItem(SESSION_ID_KEY);
      if (currentSessionId) {
        try {
          await paidolService.createOrGetPaidolSession({ sessionId: currentSessionId, paidolId, userId });
        } catch (sessionError) {
          captureException(sessionError);
        }
      }
    }

    await backupLocalStorage();
    return cognitoUser.attributes;
  } catch (error) {
    // authenticateWithPasskey calls Auth.signIn(username) which opens a
    // CUSTOM_AUTH flow; Amplify keeps that CognitoUser in a pending
    // CUSTOM_CHALLENGE state. When it fails (NO_PASSKEY / cancel), that stale
    // pending user makes the subsequent password Auth.signIn hang — the login
    // spinner never resolves in the iOS WebView. Clear it so the password
    // fallback starts from a clean slate.
    //
    // Scoped to the native mobile shell (the only place the hang occurs): on
    // desktop web this rejection is the common NO_PASSKEY fallback for any user
    // without a passkey, and Auth.signOut() there clears the shared Amplify
    // local store — which would sign the user out of a session open in another
    // tab. The web password fallback already worked without a signOut (SEE-213).
    if (isMobileAppShell()) {
      try {
        await Auth.signOut();
      } catch {
        /* nothing to sign out — safe to ignore */
      }
    }
    return rejectWithValue(serializeError(error as Error));
  }
});

export const refreshUserSession = createAsyncThunk('auth/refreshUserSession', async (_, { dispatch }) => {
  const cognitoUser = await Auth.currentAuthenticatedUser();
  const currentSession = await Auth.currentSession();

  await new Promise<void>((resolve, reject) => {
    cognitoUser.refreshSession(
      currentSession.getRefreshToken(),
      (err: Error, _session: unknown) => {
        if (err) {
          captureException(err);
          logger().error(err);
          reject(err);
          return;
        }

        // Do not redirect on refresh - preserve current URL (e.g. card groups) per SPE-2983
        localStorage.setItem(IS_INIITAL_ACCESS_LOCAL_STORAGE_KEY, 'false');
        resolve();
      },
      { paidolSessionId: localStorage.getItem(PAIDOL_SESSION_ID_KEY) ?? '' }
    );
  });

  const session = await Auth.currentSession();

  await backupLocalStorage();

  return session;
});

interface ForgotPasswordArgs {
  username: string;
}

export const forgotPassword = createAsyncThunk<unknown, ForgotPasswordArgs, { rejectValue: ErrorLike }>(
  'auth/forgotPassword',
  async ({ username }: ForgotPasswordArgs, { rejectWithValue }) => {
    return Auth.forgotPassword(username).catch((error) => {
      captureException(error, {
        extra: { username },
      });
      return rejectWithValue(serializeError(error));
    });
  }
);

interface ForgotPasswordSubmitArgs {
  username: string;
  code: string;
  new_password: string;
}

export const forgotPasswordSubmit = createAsyncThunk<
  string,
  ForgotPasswordSubmitArgs,
  { rejectValue: ErrorLike }
>(
  'auth/forgotPasswordSubmit',
  async ({ username, code, new_password }: ForgotPasswordSubmitArgs, { rejectWithValue, dispatch }) => {
    return Auth.forgotPasswordSubmit(username, code, new_password)
      .then((response) => {
        // Pre-fill username on Login page after submit.
        dispatch(rememberUsername({ username }));
        return response;
      })
      .catch((error) => {
        captureException(error, {
          extra: { username },
        });
        return rejectWithValue(serializeError(error));
      });
  }
);

interface SignUpArgs {
  firstName: string;
  middleName?: string;
  lastName: string;
  suffix?: string;
  username: string;
  email: string;
  password: string;
  birthdate: string;
  phoneNumber: string;
  address: string;
  complement: string;
  postalCode: string;
  city: string;
  state: string;
  acceptSmsNotifications: boolean;
  isSSO?: boolean;
}

export const signUp = createAsyncThunk<unknown, SignUpArgs, { rejectValue: ErrorLike }>(
  'auth/signUp',
  async (
    {
      firstName,
      middleName,
      lastName,
      suffix,
      username,
      email,
      password,
      birthdate,
      phoneNumber,
      address,
      complement,
      postalCode,
      city,
      state,
      acceptSmsNotifications,
      isSSO,
    }: SignUpArgs,
    { rejectWithValue, dispatch }
  ) => {
    const profileData: Record<string, unknown> = { acceptSmsNotifications };
    if (middleName) {
      profileData.middleName = middleName;
    }
    if (suffix) {
      profileData.suffix = suffix;
    }

    const isSSORegistration = isSSO === true;

    return Auth.signUp({
      password,
      username,
      attributes: {
        given_name: firstName,
        family_name: lastName,
        preferred_username: username,
        email,
        address: JSON.stringify({
          streetAddress: `${address}`,
          extendedAddress: `${complement}`,
          postalCode,
          locality: city,
          region: state,
        }),
        phone_number: phoneNumber, // format: '+15555555555'
        birthdate, // format: 'YYYY-MM-DD'
        profile: JSON.stringify(profileData), // Store middleName, suffix, and SMS consent in profile field
      },
      ...(isSSORegistration && {
        validationData: { is_sso_registration: 'true' },
      }),
    })
      .then((response) => {
        // Pre-fill username on Confirm page after submit.
        dispatch(rememberUsername({ username }));
        return response;
      })
      .catch((error) => {
        captureException(error, {
          extra: {
            username,
          },
        });
        rejectWithValue(error);
      });
  }
);

interface ConfirmSignUpArgs {
  username: string;
  code: string;
}

export const confirmSignUp = createAsyncThunk<unknown, ConfirmSignUpArgs, { rejectValue: ErrorLike }>(
  'auth/verifyUser',
  async ({ username, code }: ConfirmSignUpArgs, { rejectWithValue, dispatch }) => {
    const safeCode = code.trim();
    return Auth.confirmSignUp(username, safeCode)
      .then((response) => {
        // Pre-fill username on Login page after submit.
        dispatch(rememberUsername({ username }));
        return response;
      })
      .catch((error) => {
        captureException(error, {
          extra: { username },
        });
        return rejectWithValue(serializeError(error));
      });
  }
);

interface ResendSignUpArgs {
  username: string;
}

export const resendSignUp = createAsyncThunk<unknown, ResendSignUpArgs, { rejectValue: ErrorLike }>(
  'auth/resendSignUp',
  async ({ username }: ResendSignUpArgs, { rejectWithValue }) => {
    return Auth.resendSignUp(username).catch((error) => {
      captureException(error, {
        extra: { username },
      });
      return rejectWithValue(serializeError(error));
    });
  }
);

export const updateUserAttributes = createAsyncThunk<unknown, UserAttributes>(
  'auth/updateUserAttributes',
  async (newAttributes, { dispatch }) => {
    return Auth.currentAuthenticatedUser().then((user) => {
      // Cognito rejects updates containing non-mutable attributes — notably
      // `identities` on SSO-linked accounts (also `sub` and the verified flags).
      // Strip them; Cognito merges partial updates, so unspecified attributes
      // are preserved anyway. `phone_number` is stripped too: it is a security
      // factor now owned solely by the gated phone-change wizard (SEE-221), and
      // echoing the cached (stale) value here would revert the wizard's change
      // and re-set phone_number_verified=false right after the wizard verified it.
      const NON_MUTABLE = ['identities', 'sub', 'email_verified', 'phone_number_verified', 'phone_number'];
      const mutableAttributes = Object.fromEntries(
        Object.entries(user.attributes).filter(([key]) => !NON_MUTABLE.includes(key))
      );
      return Auth.updateUserAttributes(user, {
        ...mutableAttributes,
        ...newAttributes,
      })
        .then(() => dispatch(getCurrentUser()))
        .catch((error) => {
          captureException(error, {
            extra: {
              operation: 'updateUserAttributes',
              updatedFields: Object.keys(newAttributes),
            },
          });
          throw error;
        });
    });
  }
);

export const verifyCurrentUserAttributeSubmit = createAsyncThunk<unknown, string>(
  'auth/verifyCurrentUserAttributeSubmit',
  async (code, { dispatch }) => {
    return Auth.verifyCurrentUserAttributeSubmit('email', code)
      .then(() => dispatch(getCurrentUser()))
      .catch((error) => {
        captureException(error, {
          extra: { code },
        });
        throw error;
      });
  }
);

interface ChangePasswordArgs {
  oldPassword: string;
  newPassword: string;
}

export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async ({ oldPassword, newPassword }: ChangePasswordArgs) => {
    return Auth.currentAuthenticatedUser().then((user) => {
      return Auth.changePassword(user, oldPassword, newPassword).catch((error) => {
        captureException(error);
        throw error;
      });
    });
  }
);

/**
 * Reset actions for slices that hold paidol-scoped data. Do NOT add
 * user-global slices (userProfileSlice, userCompaniesSlice, profileSlice) —
 * resetting them on company switch would wipe the company list itself and
 * break the post-switch UI.
 */
const paidolScopedResetActions = () => [
  // Main store slices
  resetDialogSlice(),
  resetFeatureFlagsSlice(),
  resetOnboardSlice(),
  resetWalletSlice(),
  resetSearchAndFilter(),
  resetFlexCodingSlice(),
  // SEE-1320: these three survived a same-tab sign-out (Logout and SessionTimeoutGuard do not
  // reload), so the next user saw the previous company's flex field sidesheet, job codes and
  // transaction filter values.
  resetFlexFieldSlice(),
  resetJobCodeSlice(),
  clearFilterOptions(),
  // SEE-1828: which transactions have a split draft is per company.
  resetSplitDraftsSlice(),

  // Pages store slices
  resetReimbursementsSlice(),
  resetAgaveSlice(),
  resetBudgetWidgetSlice(),
  resetDownloadTransactions(),
  resetFundingSlice(),
  resetGroupsSlice(),
  resetCardSlice(),
  resetPriorityActionsSlice(),
  resetProcoreSlice(),
  resetReviewOnboardSlice(),
  resetSpecificCard(),
  resetSpendRulesSlice(),
  resetSpendWidgetSlice(),
  resetTopMerchantsWidgetSlice(),
  resetTransactionsSlice(),
  resetGroupCards(),
  resetAssinableCards(),
  resetProjectGroupsSlice(),
  resetUsersSlice(),

  // Overview store slices
  resetUnreadMessagesSlice(),
  resetOverviewPriorityActionsSlice(),

  // Payables store slices
  resetDataExportSlice(),
];

// Auth-only keys (SESSION_ID, federated session, IS_INIITAL_ACCESS) are
// intentionally NOT in this list — those persist across paidol switches
// and are only cleared on signOut.
const PAIDOL_SCOPED_LOCAL_STORAGE_KEYS = [
  PROCORE_TOKENS_KEY,
  'selectedExternalAccountId',
  'plaid_link_token',
];

const PAIDOL_SCOPED_SESSION_STORAGE_KEYS = [MY_CARDS_PAGE_TOKEN_KEY];

export const resetAllPaidolScopedState = (dispatch: (action: unknown) => unknown): void => {
  paidolScopedResetActions().forEach((action) => dispatch(action));
  PAIDOL_SCOPED_LOCAL_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  PAIDOL_SCOPED_SESSION_STORAGE_KEYS.forEach((key) => sessionStorage.removeItem(key));
  invalidateAgaveOptionsCache();
  invalidateFlexOptionsCache();
  clearWaitingOnCache();
};

export const signOut = createAsyncThunk<unknown, void, { rejectValue: ErrorLike }>(
  'auth/signOut',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      // SEE-219: clear the inactivity stamp FIRST — for SSO sessions
      // Auth.signOut() below performs a full-page redirect to Cognito
      // /logout, and anything after it races the page unload. A surviving
      // stale stamp made the boot check kill every fresh SSO login (lockout
      // loop). Clearing early is safe: if sign-out fails, the checker simply
      // restarts the idle window.
      clearLastActivityAt();

      // Notify mobile app to clear localStorage before signing out
      notifyMobileAppToClearLocalStorage();

      // Detect if this was an SSO session before clearing tokens
      const wasFederatedSession = Boolean(localStorage.getItem(`${SESSION_ID_KEY}_federated`));

      await Auth.signOut();

      setUser(null);
      amplitudeReset();

      // Auth-only localStorage cleanup (NOT shared with switch — switch keeps
      // the user logged in, only signOut tears down the auth session itself).
      localStorage.removeItem(IS_INIITAL_ACCESS_LOCAL_STORAGE_KEY);
      localStorage.removeItem(`${SESSION_ID_KEY}_federated`);
      localStorage.removeItem(PAIDOL_SESSION_ID_KEY);
      // SEE-213: clear the per-tab "passkey prompt already shown" guards so a
      // fresh login in the same tab is re-evaluated for the prompt. Without
      // this, sessionStorage persists across logout and silently suppresses the
      // prompt on the next login (the guard only exists to stop a /landing ->
      // /setup-passkey redirect loop within a single login session). The
      // per-device created/opt-out record (localStorage) is intentionally NOT
      // cleared — that choice should survive logout.
      Object.keys(sessionStorage)
        .filter((key) => key.startsWith('passkeyPromptShown:'))
        .forEach((key) => sessionStorage.removeItem(key));
      clearCompanySelection();
      // SEE-1319: drop presigned file URLs minted for this user so the next user in
      // this tab cannot be served them (sign-out resets Redux without a reload). Part
      // of the auth-session teardown, so it runs even if a later slice reset throws.
      fileAccessService.clear();

      // After SSO sign-out, clear Cognito's cached tokens to prevent them from
      // interfering with the next non-SSO login attempt.
      if (wasFederatedSession) {
        Object.keys(localStorage)
          .filter((key) => key.startsWith('CognitoIdentityServiceProvider'))
          .forEach((key) => localStorage.removeItem(key));
      }

      // Paidol-scoped state (shared with switch flow).
      resetAllPaidolScopedState(dispatch);

      // User-global resets that ONLY apply on signOut, never on switch:
      // resetting these on switch would wipe the company list and user
      // profile, breaking the post-switch UI.
      [resetUserProfileSlice(), resetUserCompaniesSlice(), resetProfileSlice()].forEach((action) =>
        dispatch(action)
      );
    } catch (error) {
      captureException(error);
      return rejectWithValue(serializeError(error as Error));
    }
  }
);

interface RememberUsernameArgs {
  username: string;
  persist?: boolean;
}

export const rememberUsername = createAsyncThunk(
  'auth/rememberUsername',
  async ({ username, persist = false }: RememberUsernameArgs, { dispatch }) => {
    if (persist) {
      // Only use `persist` option if user has checked 'Remember Me'
      // option during sign-in
      localStorage.setItem(LOCALSTORAGE_KEY, username);
    } else {
      localStorage.removeItem(LOCALSTORAGE_KEY);
    }
    dispatch(updateUsername(username));
  }
);

export const forgetUsername = createAsyncThunk('auth/forgetUsername', async (_, { dispatch }) => {
  localStorage.removeItem(LOCALSTORAGE_KEY);
  dispatch(deleteUsername());
});

export const createSessionId = (sessionId?: string) => {
  const currentSessionId = sessionId ?? uuidv4();
  localStorage.setItem(SESSION_ID_KEY, currentSessionId);
  return currentSessionId;
};

export const savePaidolSessionId = createAsyncThunk(
  'auth/savePaidolSessionId',
  async (paidolId: string, { dispatch }) => {
    // Cleared in `finally` so a backend rejection (membership check failure,
    // network error) still re-enables the switcher dropdown.
    dispatch(setIsSwitching(true));
    try {
      return await withTimeout(Auth.currentSession(), 8000, 'Auth.currentSession')
        .then((currentCognitoUserSession) => {
          return currentCognitoUserSession.getAccessToken().payload.sub;
        })
        .then(async (userId) => {
          const sessionId = localStorage.getItem(SESSION_ID_KEY);
          if (!sessionId) return;

          const paidolSessionId = `${paidolId}:${sessionId}`;
          const currentPaidolSessionId = localStorage.getItem(PAIDOL_SESSION_ID_KEY);
          if (currentPaidolSessionId !== paidolSessionId) {
            // Backend call before localStorage write: a 403 from the
            // membership check throws here, so localStorage and Redux stay
            // consistent with the user's actual paidol.
            await paidolService.createOrGetPaidolSession({
              sessionId,
              paidolId,
              userId,
            });
            localStorage.setItem(PAIDOL_SESSION_ID_KEY, paidolSessionId);

            // Re-mint the JWT so cognito:groups[0] matches the new paidol.
            await dispatch(refreshUserSession()).unwrap();

            resetAllPaidolScopedState(dispatch);

            // Reloading here would re-introduce SPE-1474: AuthProvider's
            // bootstrap dispatches this thunk during login, and a reload
            // mid-bootstrap loops the user back to login. Reload is the
            // call site's responsibility (switcher onChange, 421 reroute).
          }

          // Sync Redux with the active paidol REGARDLESS of whether a backend
          // rotation was needed. AuthProvider's bootstrap dispatches us with
          // the paidol that's already in localStorage; that path skips the
          // inner block, but RequireAuth's `!selectedPaidolId` gate keeps the
          // loading spinner up forever unless we still set the Redux state.
          dispatch(setPaidolSessionId(paidolSessionId));
          dispatch(setSelectedPaidolId(paidolId));
        });
    } finally {
      dispatch(setIsSwitching(false));
    }
  }
);

export const checkSSODomainThunk = createAsyncThunk(
  'auth/checkSSODomain',
  async ({ email }: { email: string }) => {
    return userService.checkSSODomain({ email });
  }
);

export const resolveEmailToUsernameThunk = createAsyncThunk(
  'auth/resolveEmailToUsername',
  async ({ email }: { email: string }) => {
    return userService.resolveEmailToUsername({ email });
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setPaidolSessionId: (state, action: PayloadAction<string | undefined>) => {
      state.paidolSessionId = action.payload;
    },
    updateUsername: (state, action: PayloadAction<string>) => {
      state.username = action.payload;
    },
    deleteUsername: (state) => {
      state.username = undefined;
    },
    // SEE-221: record a successful step-up MFA (e.g. SMS OTP). Pass Date.now().
    setMfaVerified: (state, action: PayloadAction<number>) => {
      state.mfaVerifiedAt = action.payload;
    },
    clearMfaVerified: (state) => {
      state.mfaVerifiedAt = undefined;
    },
    // SEE-221: store the signed grant from verifyBankLinkStepUp so the next Plaid
    // link-token request can present it to the server-side gate.
    setStepUpGrant: (state, action: PayloadAction<string | undefined>) => {
      state.stepUpGrant = action.payload;
    },
    // SEE-1350: SSO account verification result (see AuthProvider gate).
    setSsoMismatch: (state, action: PayloadAction<AuthState['ssoMismatch']>) => {
      state.ssoMismatch = action.payload;
    },
    clearSsoMismatch: (state) => {
      state.ssoMismatch = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCurrentUser.rejected, (state, action) => {
      if (state.user) {
        state.didSessionExpire = true;
      }
    });

    builder.addCase(signOut.fulfilled, (state) => {
      state.user = undefined;
      state.didUserSignOut = true;
      state.didSessionExpire = false;
      state.isAuthStateUnknown = false;
      state.paidolSessionId = undefined;
      state.refreshingUserSession = false;
      state.mfaVerifiedAt = undefined;
      state.stepUpGrant = undefined;
      state.ssoMismatch = undefined;
    });

    // A fresh login starts with a clean slate: any prior session's step-up is
    // void, whichever method authenticated it. (getCurrentUser/refresh
    // intentionally preserve mfaVerifiedAt so the freshness window survives
    // token refreshes within the same session.)
    builder.addCase(signIn.fulfilled, (state) => {
      state.mfaVerifiedAt = undefined;
      state.stepUpGrant = undefined;
    });
    builder.addCase(signInWithPasskey.fulfilled, (state) => {
      state.mfaVerifiedAt = undefined;
      state.stepUpGrant = undefined;
    });

    builder.addCase(refreshUserSession.pending, (state) => {
      state.refreshingUserSession = true;
    });

    builder.addCase(refreshUserSession.fulfilled, (state) => {
      state.refreshingUserSession = false;
    });

    builder.addCase(refreshUserSession.rejected, (state) => {
      state.refreshingUserSession = false;
    });

    builder.addCase(signOut.pending, (state) => {
      state.refreshingUserSession = false;
    });

    // SEE-219: a FAILED sign-out must still tear down local auth state. By the
    // time the thunk can reject, tokens/storage are partially cleared — keeping
    // `user` set leaves a phantom "authenticated" tab whose RequireAuth bounces
    // the /login page away (dead-token limbo until the next poll). Mirror the
    // fulfilled teardown instead.
    builder.addCase(signOut.rejected, (state) => {
      state.user = undefined;
      state.didUserSignOut = true;
      state.didSessionExpire = false;
      state.isAuthStateUnknown = false;
      state.paidolSessionId = undefined;
      state.refreshingUserSession = false;
      state.mfaVerifiedAt = undefined;
      state.stepUpGrant = undefined;
      // SEE-1350: must mirror fulfilled here too. The mismatch gate renders a
      // full-screen overlay and deliberately keeps it up until sign-out
      // navigates away; leaving ssoMismatch set on a REJECTED sign-out strands
      // the user under that overlay with no buttons and no relaunch.
      state.ssoMismatch = undefined;
    });

    builder.addMatcher(
      isAnyOf(
        getCurrentUser.fulfilled,
        signIn.fulfilled,
        signInWithPasskey.fulfilled,
        refreshUserSession.fulfilled
      ),
      (state, action) => {
        state.isAuthStateUnknown = false;
        state.didSessionExpire = false;
        state.didUserSignOut = false;
        state.refreshingUserSession = false;
        state.user = { ...state.user, ...action.payload };
      }
    );

    builder.addMatcher(
      isAnyOf(getCurrentUser.rejected, signIn.rejected, refreshUserSession.rejected),
      (state, action) => {
        state.isAuthStateUnknown = false;
        state.user = undefined;
        state.refreshingUserSession = false;
      }
    );
  },
});

export const {
  updateUsername,
  deleteUsername,
  setPaidolSessionId,
  setSsoMismatch,
  clearSsoMismatch,
  setMfaVerified,
  clearMfaVerified,
  setStepUpGrant,
} = authSlice.actions;

export const selectAuth = (state: RootState): AuthState => state.auth as AuthState;

export default authSlice.reducer;
